import { Button, SkeletonLoader } from '@toggle/design-system';
import { useDisableScroll } from '@toggle/helpers';
import {
  Entity,
  ExploreInsightCondition,
  ExploreInsightDirection,
} from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useEntity } from '~/hooks/use-entities';
import { useInsightsFullDrawer } from '~/hooks/use-insights-full-drawer/useInsightsFullDrawer';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { useAssetOverviewNavigation } from '~/widgets/asset-overview-widget/hooks/use-asset-overview-navigation/useAssetOverviewNavigation';

import { InsightsInfo } from './insights-info/InsightsInfo';
import { InsightsLeftSection } from './insights-left-section/InsightsLeftSection';
import * as S from './InsightsDrawer.styles';

export interface IconConfig {
  conditions: ExploreInsightCondition[];
  direction: ExploreInsightDirection;
  isCombined?: boolean;
  isMatched?: boolean;
  layout?: 'vertical' | 'horizontal';
}

interface AssetDrawerProps {
  entityTag: string;
}

export const InsightsDrawer = ({ entityTag }: AssetDrawerProps) => {
  const { params } = useQueryParams();
  const { t } = useTranslation(['widget']);
  const { closeDrawer } = useInsightsFullDrawer();
  const { data, isLoading } = useEntity(entityTag);
  const entity = data?.data;
  const { navigateToAssetOverview } = useAssetOverviewNavigation(
    data?.data as Entity
  );
  useDisableScroll();

  const [iconConfig, setIconConfig] = useState<IconConfig>();

  return (
    <div data-testid="insights-drawer">
      <S.StyledDrawer
        isOpen
        handleCloseClick={closeDrawer}
        headerButton={
          <Button
            iconName="Expand"
            label={t('widget:assetOverview.assetOverview')}
            onClick={navigateToAssetOverview}
            variant="empty"
            size="xsmall"
          />
        }
      >
        <>
          <WithFooterWrapper>
            {isLoading ? (
              <SkeletonLoader
                data-testid="insight-header-loading"
                areas={[
                  {
                    styles: {
                      width: '100%',
                      height: 40,
                    },
                  },
                ]}
              />
            ) : (
              <>
                {entity && (
                  <S.StyledEntityHeader
                    entity={entity}
                    isDrawer
                    headerButton={<></>}
                  />
                )}
              </>
            )}
            <S.SectionWrapper>
              <S.Section>
                {entity && (
                  <InsightsLeftSection
                    entity={entity}
                    activeInsightId={params.activeInsightId ?? undefined}
                    handleSetIconConfig={setIconConfig}
                  />
                )}
              </S.Section>
              <S.Section>
                <InsightsInfo
                  insightId={params.activeInsightId}
                  iconConfig={iconConfig}
                />
              </S.Section>
            </S.SectionWrapper>
          </WithFooterWrapper>
        </>
      </S.StyledDrawer>
    </div>
  );
};
